/**
 * HEAREN BUILD 52.5 - Tinnitus Display Utilities
 * 
 * Single source of truth for tinnitus correction display.
 * Ensures consistent quantization (0.5 dB steps) and formatting across:
 * - Radio player
 * - Soundscapes player
 * - Audio Profile Modal
 * - Lab Settings
 * - Tinnitus badges
 * 
 * RULES:
 * 1. All dB values are quantized to 0.5 dB steps
 * 2. Display always matches the applied value
 * 3. Badges show when correction ≠ 0
 */

/**
 * Quantize a dB value to the nearest step (default 0.5 dB)
 * This is the SINGLE function used for both audio engine and display
 * 
 * @param {number} db - Raw dB value
 * @param {number} step - Quantization step (default 0.5)
 * @returns {number} - Quantized dB value
 * 
 * @example
 * quantizeDb(1.3)  // → 1.5
 * quantizeDb(1.7)  // → 1.5
 * quantizeDb(2.0)  // → 2.0
 * quantizeDb(-0.8) // → -1.0
 */
export const quantizeDb = (db, step = 0.5) => {
  if (db === null || db === undefined || isNaN(db)) return 0;
  return Math.round(db / step) * step;
};

/**
 * Format a quantized dB value for display
 * Shows decimals only when needed (e.g., 1.5 but not 2.0)
 * 
 * @param {number} db - dB value (will be quantized)
 * @param {Object} options - Formatting options
 * @param {boolean} options.showSign - Show + for positive values (default: true)
 * @param {boolean} options.showUnit - Append "dB" unit (default: true)
 * @returns {string} - Formatted string
 * 
 * @example
 * formatDb(1.5)                    // → "+1.5 dB"
 * formatDb(2.0)                    // → "+2 dB"
 * formatDb(-1)                     // → "-1 dB"
 * formatDb(1.5, { showSign: false }) // → "1.5 dB"
 * formatDb(1.5, { showUnit: false }) // → "+1.5"
 */
export const formatDb = (db, options = {}) => {
  const { showSign = true, showUnit = true } = options;
  
  const quantized = quantizeDb(db);
  
  // Format number: show decimal only if not integer
  const numStr = Number.isInteger(quantized) 
    ? quantized.toString() 
    : quantized.toFixed(1);
  
  // Add sign prefix
  let result = '';
  if (showSign && quantized > 0) {
    result = '+' + numStr;
  } else {
    result = numStr;
  }
  
  // Add unit suffix
  if (showUnit) {
    result += ' dB';
  }
  
  return result;
};

/**
 * Determine the correction type based on gain value
 * 
 * @param {number} db - Gain value in dB
 * @returns {'boost' | 'attenuation' | 'none'} - Correction type
 */
export const getCorrectionType = (db) => {
  const quantized = quantizeDb(db);
  if (quantized > 0) return 'boost';
  if (quantized < 0) return 'attenuation';
  return 'none';
};

/**
 * Get badge info for tinnitus correction display
 * BUILD 58: Show qualitative text instead of numeric dB values
 * 
 * @param {number} gainDb - Gain value from lab program
 * @param {string} lang - Language code ('fr' or 'en')
 * @returns {Object} - Badge display info
 * 
 * @example
 * getTinnitusBadgeInfo(1.5, 'fr')
 * // → { visible: true, type: 'boost', label: 'Profil acouphènes actif', icon: '🎧' }
 * 
 * getTinnitusBadgeInfo(-2, 'en')
 * // → { visible: true, type: 'attenuation', label: 'Tinnitus profile active', icon: '🎧' }
 */
export const getTinnitusBadgeInfo = (gainDb, lang = 'fr') => {
  const quantized = quantizeDb(gainDb);
  const type = getCorrectionType(quantized);
  
  if (type === 'none') {
    return {
      visible: true,
      type: 'none',
      label: '',
      value: '',
      icon: '',
      quantizedGain: 0
    };
  }
  
  // BUILD 58: Qualitative text instead of numeric dB
  const labelFr = 'Profil acouphènes actif';
  const labelEn = 'Tinnitus profile active';
  const label = lang === 'en' ? labelEn : labelFr;
  
  // BUILD 66.3: Show direction icon based on correction type
  // 🔼 for stimulation (boost), 🔽 for attenuation
  const icon = type === 'boost' ? '🔼' : '🔽';
  
  return {
    visible: true,
    type,
    label: label,
    value: '', // BUILD 58: No numeric value displayed
    valueShort: '',
    icon: icon, // BUILD 66.3: Direction-based icon
    quantizedGain: quantized
  };
};

/**
 * Format frequency for display (always integer Hz)
 * 
 * @param {number} freq - Frequency in Hz
 * @returns {string} - Formatted frequency
 * 
 * @example
 * formatFrequency(6050) // → "6050 Hz"
 */
export const formatFrequency = (freq) => {
  if (!freq || isNaN(freq)) return '— Hz';
  return `${Math.round(freq)} Hz`;
};

/**
 * BUILD 65: Format tinnitus label for UI display
 * Shows ONLY the frequency, NO dB gain
 * 
 * @param {number} frequency - Frequency in Hz (from last tinnitus test)
 * @param {string} lang - Language code ('fr' or 'en')
 * @returns {string} - Formatted label "Tinnitus (XXXX Hz)" or "Acouphènes (XXXX Hz)"
 * 
 * @example
 * formatTinnitusLabel(6050) // → "Tinnitus (6050 Hz)"
 * formatTinnitusLabel(6050, 'fr') // → "Acouphènes (6050 Hz)"
 */
export const formatTinnitusLabel = (frequency, lang = 'en') => {
  if (!frequency || isNaN(frequency) || frequency <= 0) {
    return lang === 'fr' ? 'Acouphènes' : 'Tinnitus';
  }
  const freqStr = `${Math.round(frequency)} Hz`;
  const prefix = lang === 'fr' ? 'Acouphènes' : 'Tinnitus';
  return `${prefix} (${freqStr})`;
};

/**
 * BUILD 65: Format tinnitus frequency only for chart/spectrum display
 * Shows ONLY "XXXX Hz" - NO dB
 * 
 * @param {number} frequency - Frequency in Hz
 * @returns {string} - Just the frequency "XXXX Hz"
 * 
 * @example
 * formatTinnitusFrequencyOnly(6050) // → "6050 Hz"
 */
export const formatTinnitusFrequencyOnly = (frequency) => {
  if (!frequency || isNaN(frequency) || frequency <= 0) return '';
  return `${Math.round(frequency)} Hz`;
};

/**
 * Get full tinnitus profile display string
 * BUILD 65: Now shows ONLY frequency, NO dB gain
 * 
 * @param {number} frequency - Frequency in Hz
 * @param {number} gainDb - Gain in dB (IGNORED for display, kept for API compat)
 * @returns {string} - Display string with frequency only
 * 
 * @example
 * formatTinnitusProfile(6050, 2) // → "6050 Hz" (dB ignored)
 */
export const formatTinnitusProfile = (frequency, gainDb) => {
  if (!frequency || frequency <= 0) return null;
  return formatFrequency(frequency);
};

/**
 * Validate and normalize a tinnitus profile
 * Ensures all values are properly quantized
 * 
 * @param {Object} profile - Raw profile object
 * @returns {Object} - Normalized profile
 */
export const normalizeTinnitusProfile = (profile) => {
  if (!profile) return null;
  
  return {
    ...profile,
    f_t: profile.f_t ? Math.round(profile.f_t) : null,
    gain_db: quantizeDb(profile.gain_db),
    // Preserve other fields
    program_type: profile.program_type,
    enabled: profile.enabled,
    source: profile.source
  };
};

// Export all utilities
export default {
  quantizeDb,
  formatDb,
  getCorrectionType,
  getTinnitusBadgeInfo,
  formatFrequency,
  formatTinnitusLabel,
  formatTinnitusFrequencyOnly,
  formatTinnitusProfile,
  normalizeTinnitusProfile
};
